import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ArticleDto } from '../../api/ClientApi';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Chip, IconButton, Paper, TablePagination } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import ArticleForm from './ArticleForm';

import {
    MobileCardRow,
    mobileHiddenHeadSx,
    mobileTableSx,
    useIsMobile,
} from '../../common/components/mobile/MobilePrimitives';

function createData(
    id?: number,
    manufacturer?: string,
    title?: string,
    innerCode?: string,
    outterCode?: string,
    labels?: string[],
    price?: number,
) {
    return { id, manufacturer, title, innerCode, outterCode, price, labels };
}

type ArticlesTableProps = {
    articles: ArticleDto[] | null;
};

export default function ArticlesTable(props: ArticlesTableProps) {
    const { articles } = props;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const rows = articles?.map((article) => {
        return createData(
            article.id,
            article.manufacturer,
            article.title,
            article.innerCode,
            article.outterCode,
            article.labels?.map((l) => l.title ?? []).flat() ?? [],
        );
    });

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (rows?.length ?? 0)) : 0;

    const visibleRows = React.useMemo(
        () => (!rows ? [] : [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [page, rowsPerPage, articles],
    );

    React.useEffect(() => {
        if (!rows || rows?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [rows, page]);

    const [articleId, setArticleId] = React.useState<number | undefined>(undefined);

    const goToForm = (aId?: number) => {
        setArticleId(aId);
    };

    const handleCloseForm = () => {
        setArticleId(undefined);
    };

    const isMobile = useIsMobile();

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <TableContainer sx={{ margin: 'auto' }}>
                        <Table sx={mobileTableSx(650)} size="small" stickyHeader aria-label="simple table">
                            <TableHead sx={mobileHiddenHeadSx}>
                                <TableRow>
                                    <StyledTableCell>Id</StyledTableCell>
                                    <StyledTableCell align="center">Interni kod proizvoda</StyledTableCell>
                                    <StyledTableCell align="center">Naziv</StyledTableCell>
                                    <StyledTableCell align="center">Proizvođač</StyledTableCell>
                                    <StyledTableCell align="center">Šifra artikla proizvođača</StyledTableCell>
                                    <StyledTableCell align="center">Tag</StyledTableCell>
                                    <StyledTableCell align="center" sx={{ width: 100 }}>
                                        Akcija
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows?.map((row) => {
                                    if (!!row.id) {
                                        if (isMobile) {
                                            return (
                                                <MobileCardRow
                                                    key={row.id}
                                                    title={row.title ?? '/'}
                                                    subtitle={row.manufacturer}
                                                    header={
                                                        !!row.labels && row.labels.length > 0
                                                            ? row.labels.map((l) => (
                                                                  <Chip
                                                                      key={l}
                                                                      size="small"
                                                                      color="primary"
                                                                      variant="outlined"
                                                                      label={l}
                                                                  />
                                                              ))
                                                            : undefined
                                                    }
                                                    actions={
                                                        <IconButton color="primary" onClick={() => goToForm(row.id)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    }
                                                    fields={[
                                                        { label: 'Interni kod', value: row.innerCode ?? '/' },
                                                        { label: 'Šifra proizv.', value: row.outterCode ?? '/' },
                                                    ]}
                                                />
                                            );
                                        }
                                        return (
                                            <TableRow key={row.id}>
                                                <TableCell component="th" scope="row">
                                                    {row.id ?? '/'}
                                                </TableCell>
                                                <TableCell align="center">{row.innerCode ?? '/'}</TableCell>
                                                <TableCell align="center">{row.title ?? '/'}</TableCell>
                                                <TableCell align="center">{row.manufacturer ?? '/'}</TableCell>
                                                <TableCell align="center">{row.outterCode ?? '/'}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                        {!!row.labels && row.labels.length > 0
                                                            ? row.labels.map((l) => {
                                                                  return (
                                                                      <Chip
                                                                          key={l}
                                                                          color="primary"
                                                                          variant="outlined"
                                                                          label={l}
                                                                      />
                                                                  );
                                                              })
                                                            : '/'}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: 100, py: 0 }}>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => goToForm(row.id)}
                                                        sx={{ m: 0 }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                })}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: 40 * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={rows?.length ?? 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
            <ArticleForm open={!!articleId} closeForm={handleCloseForm} articleId={articleId} />
        </>
    );
}
